import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Globe, Loader2, Phone, Send, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChannelIcon } from "@/components/crm/ChannelIcon";
import { StatusBadge } from "@/components/crm/Badges";
import { Avatar, MessageBubble } from "@/components/crm/conversations/MessageBubble";
import { useRealtime } from "@/hooks/useRealtime";
import { brand } from "@/lib/brand";
import { StartWhatsAppButton, WelcomeChips } from "@/components/crm/WelcomeWhatsApp";

export const ConversationThread = ({ id, onChanged }) => {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => {
    setNotFound(false);
    api.get(`/conversations/${id}`).then((r) => { setData(r.data); onChanged(); })
      .catch((e) => e.response?.status === 404 && setNotFound(true));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [load]);
  useRealtime((ev) => { if (ev.conversation_id === id) load(); });
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [data]);

  const send = async () => {
    setSending(true);
    try {
      const { data: msg } = await api.post(`/conversations/${id}/reply`, { body: text });
      setText("");
      if (msg.delivery_status === "no_enviado") toast.warning(`Mensaje no enviado: ${brand(msg.error_reason) || "motivo desconocido"}`);
      else toast.success("Mensaje enviado");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setSending(false); }
  };

  if (notFound) return (
    <div className="flex flex-1 flex-col items-center justify-center py-20 text-center" data-testid="conversation-not-found">
      <ShieldX className="h-9 w-9 text-slate-300" /><p className="mt-3 font-semibold">Conversación no encontrada</p>
      <Link to="/crm/conversaciones" className="mt-3 text-sm text-[#1E6FD9]">Volver</Link>
    </div>
  );
  if (!data) return <div className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E6FD9]" /></div>;

  const { conversation: c, messages } = data;
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="conversation-thread">
      <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
        <Link to="/crm/conversaciones" className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 lg:hidden" data-testid="thread-back-button"><ArrowLeft className="h-4 w-4" /></Link>
        <Avatar name={c.lead_name} url={c.avatar_url} size="h-9 w-9" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold" data-testid="thread-lead-name">{c.lead_name}</p>
          <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><ChannelIcon channel={c.channel} className="h-3.5 w-3.5 text-[#1E6FD9]" /> {c.channel}</span>
            <span>· {c.seller_name || "Sin asignar"}</span>
            <StatusBadge status={c.status} testid="thread-status" />
            {c.channel === "Web" && <WelcomeChips tagStatus={c.lead_tag_status} welcomeAt={c.lead_welcome_sent_at} testid="thread-welcome-status" />}
          </p>
        </div>
        <Link to={`/crm/leads/${c.lead_id}`} className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#1E6FD9]" data-testid="thread-lead-link">
          Ficha <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="min-h-[300px] flex-1 space-y-3 overflow-y-auto bg-[#F5F7FA]/60 p-4" data-testid="thread-messages">
        {messages.map((m) => <MessageBubble key={m.id} m={m} conversationId={id} onRetried={load} />)}
        <div ref={endRef} />
      </div>
      {c.channel === "Web" ? <WebNote c={c} onDone={load} /> : <div className="border-t border-slate-100 p-3">
        <div className="flex gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe una respuesta…" className="min-h-0 flex-1 resize-none rounded-xl" data-testid="reply-input" />
          <Button onClick={send} disabled={!text.trim() || sending} className="h-auto rounded-xl bg-[#1E6FD9] px-4" data-testid="reply-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Enviar</span></>}
          </Button>
        </div>
      </div>}
    </div>
  );
};

const WebNote = ({ c, onDone }) => (
  <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-slate-100 bg-sky-50 p-4" data-testid="web-readonly-note">
    <Globe className="h-5 w-5 shrink-0 text-sky-600" />
    <p className="min-w-[220px] flex-1 text-sm text-slate-700">Este mensaje llegó por el formulario web. Llama al paciente o espera su respuesta a la bienvenida por WhatsApp.</p>
    {c.lead_phone && (
      <a href={`tel:${c.lead_phone}`} className="inline-flex items-center gap-1.5 rounded-xl bg-[#1E6FD9] px-4 py-2 text-sm font-semibold text-white" data-testid="web-call-button">
        <Phone className="h-4 w-4" /> Llamar
      </a>
    )}
    <StartWhatsAppButton leadId={c.lead_id} name={c.lead_name} phone={c.lead_phone} seed={c.lead_seed} onDone={onDone} />
    <Link to={`/crm/leads/${c.lead_id}`} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700" data-testid="web-view-lead-button">
      Ver lead
    </Link>
  </div>
);
